/* eslint-disable css-modules/no-undef-class */
import classNames from 'classnames';
import { Feature } from 'geojson';
import dynamic from 'next/dynamic';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Image from '@/components/atoms/image/image';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import Dropdown, {
  DropdownProps,
} from '@/components/molecules/dropdown/dropdown';
import HotelCard from '@/components/organisms/hotel-card/hotel-card';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Link from '@/components/utilities/link/link';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import useQueryDestinationsMap from '@/integrations/offers/destinations/map/map.hooks';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import useIsHovered from '@/libraries/hooks/use-is-hovered';
import useIsScrollCompleted from '@/libraries/hooks/use-is-scroll-completed';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import {
  destinationMapHotelCardCollectionAdapter,
  destinationMapHotelsFeatureAdapter,
  destinationMapHotelsFeatureCollectionAdapter,
  destinationMapPoisFeatureCollectionAdapter,
  isDestinationMapHotel,
  isDestinationMapPoi,
} from './destination-map.adapters';
import styles from './destination-map.module.scss';

const Map = dynamic(() => import('@/components/molecules/map/map'), {
  loading: () => <SkeletonLoader height="100%" width="100%" />,
  ssr: false,
});

export interface DestinationMapProps extends ModuleWrapperProps {
  /** The destination id */
  destinationId: Content.Components['DestinationMapModuleDto']['destinationId'];
  /** The data to show on the map as initial data */
  destinationMap: Content.Components['DestinationMapModuleDto']['destinationMap'];
  /** The filters to show */
  filters: Content.Components['DestinationMapModuleDto']['filters'];
  /** The text to show as a headline */
  headline: Content.Components['DestinationMapModuleDto']['headline'];
  /** The image to show as a placeholder */
  placeholderImage: Content.Components['DestinationMapModuleDto']['placeholderImage'];
  /** The text to show as a pre headline */
  preHeadline: Content.Components['DestinationMapModuleDto']['preHeadline'];
}

const OFFSET = 32;

const DestinationMap = ({
  preHeadline,
  destinationId,
  destinationMap,
  headline,
  filters,
  placeholderImage,
  ...rest
}: DestinationMapProps) => {
  const { t } = useI18n();
  const isLaptop = useWindowSize('laptop');
  const currencyFormatter = useCurrencyFormatter();
  const { handleUpdateIsScrolled, hotelDetails, stickyTopContentHeight } =
    usePageDetailsContext();
  const dataContainerRef = useRef<HTMLDivElement>(null);
  const stickyTopContentHeightRef = useRef(stickyTopContentHeight);

  const {
    bindRef,
    elementRef: mapWrapperRef,
    isHovered: isMapHovered,
  } = useIsHovered<HTMLDivElement>();

  const [activeItemIndex, setActiveItemIndex] = useState<number>();
  const [currentTab, setCurrentTab] = useState<'list' | 'map'>('list');
  const [destinationMapHotels, setDestinationMapHotels] = useState<
    Content.Properties.MapHotelEntry[]
  >([]);
  const [destinationMapPois, setDestinationMapPois] = useState<
    Content.Properties.MapPoiEntry[]
  >([]);
  const [hotelId, setHotelId] = useState<string>();
  const [isInitial, setIsInitial] = useState(true);
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollTarget, setScrollTarget] = useState<number | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<
    { facilityId: number; filterId: number }[]
  >([]);
  const [selectedPoiFilters, setSelectedPoiFilters] = useState<string[]>([]);
  const [showMap, setShowMap] = useState(!placeholderImage);

  const { data: dataDestinationsMap, isLoading: isLoadingDestinationsMap } =
    useQueryDestinationsMap({
      destinationId,
      selectedFilters: selectedFilters.map(filter => filter.facilityId),
    });

  const hasHookData = (destinationMapHotels?.length as number) > 0;

  useIsScrollCompleted({
    isEnabled: !!scrollTarget,
    onComplete: () => {
      setIsScrolling(false);
      setScrollTarget(null);
    },
    targetScrollY: scrollTarget ?? 0,
    threshold: 2,
  });

  useEffect(() => {
    stickyTopContentHeightRef.current = stickyTopContentHeight;
  }, [stickyTopContentHeight]);

  useEffect(() => {
    if (isScrolling) return;

    if (isMapHovered && showMap) {
      handleUpdateIsScrolled(false);

      return;
    }

    handleUpdateIsScrolled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapHovered, isScrolling]);

  useEffect(() => {
    if (!dataDestinationsMap) return;

    setDestinationMapHotels(dataDestinationsMap.filter(isDestinationMapHotel));

    setDestinationMapPois(dataDestinationsMap.filter(isDestinationMapPoi));
  }, [dataDestinationsMap]);

  const scrollToItem = (id: string) => {
    if (!dataContainerRef.current) return;

    if (!mapWrapperRef.current) return;

    const targetElement = document.getElementById(id);

    if (!targetElement) return;

    const offset =
      window.scrollY +
      targetElement.getBoundingClientRect().top -
      stickyTopContentHeightRef.current -
      OFFSET;

    setIsScrolling(true);
    setScrollTarget(offset);
    handleUpdateIsScrolled(false);

    window.scrollTo({ behavior: 'smooth', top: offset });
  };

  // TODO: remove `replaceAllFilters` when dropdown supports multiple selection
  const handleClickFilter = (
    value: { facilityId: number; filterId: number },
    options?: {
      isRadioButton?: boolean;
      replaceAllFilters?: boolean;
    },
  ) => {
    setHotelId(undefined);
    setActiveItemIndex(undefined);
    setShowMap(true);

    setSelectedFilters(previousValue => {
      const isInFilters = previousValue.some(
        previous => previous.facilityId === value.facilityId,
      );

      if (options?.replaceAllFilters) {
        return isInFilters ? [] : [value];
      }

      if (options?.isRadioButton) {
        if (value.facilityId === null) {
          return [
            ...previousValue.filter(item => item.filterId !== value.filterId),
          ];
        }

        return [
          ...previousValue.filter(item => item.filterId !== value.filterId),
          value,
        ];
      }

      if (isInFilters) {
        return [
          ...previousValue.filter(item => item.facilityId !== value.facilityId),
        ];
      }

      return [...previousValue, value];
    });
  };

  // TODO: remove `replaceAllFilters` when dropdown supports multiple selection
  const handleClickPoiFilter = (value: string, replaceAllFilters?: boolean) => {
    setShowMap(true);

    setSelectedPoiFilters(previousValue => {
      const isValueInFilters = previousValue.includes(value);

      if (replaceAllFilters) {
        return isValueInFilters ? [] : [value];
      }

      return isValueInFilters
        ? [...previousValue.filter(item => item !== value)]
        : [...previousValue, value];
    });
  };

  const handleClickMap = (feature: Feature | any) => {
    if (!destinationMapHotels) return;

    const item = destinationMapHotels.find(
      map => String(map.hotelId) === String(feature.properties.id),
    );

    if (!item) return;

    const indexOf = destinationMapHotels.indexOf(item);

    if (typeof indexOf !== 'number') return;

    setActiveItemIndex(indexOf);
    setHotelId(String(feature.properties.id));
    scrollToItem(`hotel-${feature.properties.id}`);
  };

  const filterItems = useMemo(() => {
    const items: { facilityId: number; filterId: number; label: string }[] = [];

    filters.forEach(filter => {
      filter.facilities.forEach(facility => {
        facility.facilityId &&
          items.push({
            facilityId: facility.facilityId,
            filterId: filter.id,
            label: facility.facilityName,
          });
      });
    });

    return items;
  }, [filters]);

  const poiFilters = useMemo(() => {
    const items: DropdownProps['items'] = Object.values(
      destinationMapPois.reduce((acc, item) => {
        if (!acc[item.pointOfInterestType]) {
          acc[item.pointOfInterestType] = {
            label: item.filterLabel,
            value: item.pointOfInterestType,
          };
        }

        return acc;
      }, {}) || [],
    );

    return items;
  }, [destinationMapPois]);

  // ! NOTE: Current hotel of the offer needs to be selected on initial render
  useEffect(() => {
    if (!hotelDetails) return;

    if (!destinationMapHotels?.length) return;

    if (!isInitial) return;

    const indexOf = destinationMapHotels.findIndex(
      hotel => hotel.hotelId === hotelDetails.hotelId,
    );

    setIsInitial(false);
    setActiveItemIndex(indexOf);
    setHotelId(String(hotelDetails.hotelId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationMapHotels]);

  const activeItem: Feature | any = useMemo(() => {
    return typeof activeItemIndex === 'number' &&
      destinationMapHotels?.[activeItemIndex]
      ? destinationMapHotelsFeatureAdapter(
          destinationMapHotels[activeItemIndex],
          currencyFormatter,
        )
      : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItemIndex, destinationMapHotels]);

  const formattedMapHotelsData = useMemo(() => {
    return destinationMapHotelsFeatureCollectionAdapter(
      currencyFormatter,
      destinationMapHotels,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationMapHotels]);

  const filteredDestinationMapPois = useMemo(
    () =>
      destinationMapPois.filter(item =>
        selectedPoiFilters.includes(item.pointOfInterestType),
      ),
    [destinationMapPois, selectedPoiFilters],
  );

  const formattedMapPoisData = useMemo(() => {
    return destinationMapPoisFeatureCollectionAdapter(
      filteredDestinationMapPois,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredDestinationMapPois]);

  return (
    <ModuleWrapper {...rest} className={styles.component}>
      {(preHeadline || headline) && (
        <Container className={styles.textContainer}>
          {preHeadline && (
            <Typography className={styles.preHeadline} tagStyle="subHeading">
              {preHeadline}
            </Typography>
          )}

          {headline && (
            <Typography className={styles.headline} tagStyle="displayMedium">
              {headline}
            </Typography>
          )}
        </Container>
      )}

      {filters && filters.length > 0 && (
        <Container className={styles.filtersContainer}>
          {isLaptop ? (
            <div className={styles.filtersWrapper}>
              <Typography tagStyle="bodyMediumBold">
                {t('items.modules.destinationMap.filterResults')}
              </Typography>

              <div className={styles.filters}>
                {filters.map((filter, filterIndex) => (
                  <div key={filterIndex} className={styles.filterGroup}>
                    {filter.facilities.map((item, itemIndex) => (
                      <button
                        key={itemIndex}
                        className={classNames(styles.filter, {
                          [styles.isActive]:
                            (item.facilityId === null &&
                              !selectedFilters.some(
                                selectedFilter =>
                                  selectedFilter.filterId === filter.id,
                              )) ||
                            (item.facilityId &&
                              selectedFilters.some(
                                selectedFilter =>
                                  selectedFilter.facilityId === item.facilityId,
                              )),
                        })}
                        onClick={() =>
                          handleClickFilter(
                            {
                              facilityId: item.facilityId as number,
                              filterId: filter.id,
                            },
                            { isRadioButton: filter.showAsRadioButtons },
                          )
                        }
                      >
                        <Typography tagStyle="bodyMediumBold">
                          {item.facilityName}
                        </Typography>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <Dropdown<number>
              className={styles.dropdown}
              items={filterItems.map(filter => ({
                label: filter.label,
                value: filter.facilityId,
              }))}
              label={t('items.modules.destinationMap.filterResults')}
              onChange={value =>
                handleClickFilter(
                  {
                    facilityId: value,
                    filterId: Number(
                      filterItems.find(filter => filter.filterId === value)
                        ?.filterId,
                    ),
                  },
                  { replaceAllFilters: true },
                )
              }
              value={selectedFilters[0]?.facilityId}
            />
          )}

          {!isLaptop && (
            <div className={styles.layoutPicker}>
              <button
                className={classNames(styles.layoutPickerItem, {
                  [styles.isActive]: currentTab === 'map',
                })}
                onClick={() => setCurrentTab('map')}
              >
                <Icon name="Location" size={24} />
              </button>

              <button
                className={classNames(styles.layoutPickerItem, {
                  [styles.isActive]: currentTab === 'list',
                })}
                onClick={() => setCurrentTab('list')}
              >
                <Icon name="ListView" size={24} />
              </button>
            </div>
          )}
        </Container>
      )}

      <div ref={dataContainerRef}>
        <Container>
          <Row>
            {(isLaptop || currentTab === 'list') && (
              <Column width={{ desktop: 6, laptop: 4 }}>
                <Row className={styles.itemsRow}>
                  {/* if JS is disabled we display the hotel links we receive from the initial server call
                   * we do this for SEO purposes, if that is also empty we display an empty message */}
                  <noscript>
                    {destinationMap.entries.length > 0 ? (
                      (
                        destinationMap.entries as Content.Properties.MapHotelEntry[]
                      ).map((entry, entryIndex) => (
                        <Column
                          key={entryIndex}
                          className={styles.itemsColumn}
                          width={{ laptop: 12, tablet: 6 }}
                        >
                          <Link url={entry.url}>
                            <Typography tagStyle="bodyMedium">
                              {entry.hotelName}
                            </Typography>
                          </Link>
                        </Column>
                      ))
                    ) : (
                      <Column
                        className={classNames(
                          styles.itemsColumn,
                          styles.isEmpty,
                        )}
                      >
                        <Typography tagStyle="bodyLargeBold">
                          {t('items.modules.destinationMap.noResults')}
                        </Typography>
                      </Column>
                    )}
                  </noscript>

                  {isLoadingDestinationsMap &&
                    !hasHookData &&
                    Array.from({ length: 4 }).map((_, loaderIndex) => (
                      <Column
                        key={loaderIndex}
                        className={styles.itemsColumn}
                        width={{ laptop: 12, tablet: 6 }}
                      >
                        <HotelCard className={styles.loadingItem} isLoading />
                      </Column>
                    ))}

                  {!hasHookData && !isLoadingDestinationsMap && (
                    <Column
                      className={classNames(styles.itemsColumn, styles.isEmpty)}
                    >
                      <Typography tagStyle="bodyLargeBold">
                        {t('items.modules.destinationMap.noResults')}
                      </Typography>
                    </Column>
                  )}

                  {hasHookData &&
                    destinationMapHotelCardCollectionAdapter(
                      destinationMapHotels,
                    ).map((item, itemIndex) => (
                      <Column
                        key={itemIndex}
                        className={styles.itemsColumn}
                        width={{ laptop: 12, tablet: 6 }}
                      >
                        <HotelCard
                          {...item}
                          id={`hotel-${item.id}`}
                          isActive={String(item.id) === String(hotelId)}
                          onClick={() => {
                            setHotelId(String(item.id));
                            setActiveItemIndex(itemIndex);
                            setShowMap(true);
                          }}
                        />
                      </Column>
                    ))}
                </Row>
              </Column>
            )}

            {(isLaptop || currentTab === 'map') && (
              <Column
                className={styles.mapColumn}
                width={{ desktop: 6, laptop: 8 }}
              >
                {poiFilters.length > 0 && (
                  <div className={styles.poiContainer}>
                    {isLaptop ? (
                      <React.Fragment>
                        <Typography
                          className={styles.poiHeader}
                          tagStyle="bodyMediumBold"
                        >
                          {`${t('items.maps.mapFilters')}`}
                        </Typography>

                        <div className={styles.filterGroup}>
                          {poiFilters.map((item, index) => (
                            <button
                              key={index}
                              className={classNames(styles.filter, {
                                [styles.isActive]: selectedPoiFilters.includes(
                                  item.value,
                                ),
                              })}
                              onClick={() => handleClickPoiFilter(item.value)}
                            >
                              <Typography tagStyle="bodyMediumBold">
                                {item.label}
                              </Typography>
                            </button>
                          ))}
                        </div>
                      </React.Fragment>
                    ) : (
                      <Dropdown<string>
                        className={styles.dropdown}
                        items={poiFilters}
                        label={t('items.maps.mapFilters')}
                        onChange={value => handleClickPoiFilter(value, true)}
                        value={selectedPoiFilters[0]}
                      />
                    )}
                  </div>
                )}

                <div
                  ref={bindRef}
                  className={styles.mapWrapper}
                  style={{ top: `${stickyTopContentHeight + OFFSET}px` }}
                >
                  {showMap ? (
                    <React.Fragment>
                      <Map
                        key={`destination-map-${dataDestinationsMap?.length ?? 0}`}
                        activeItem={activeItem}
                        controlPosition={isLaptop ? 'bottom-right' : undefined}
                        items={{
                          markers: formattedMapHotelsData,
                          pois: formattedMapPoisData,
                        }}
                        onClickMarker={handleClickMap}
                      />

                      {!destinationMapHotels?.length && (
                        <div className={styles.mapTextWrapper}>
                          <Typography
                            className={styles.mapText}
                            tagStyle="bodyLargeBold"
                          >
                            {t('items.modules.destinationMap.noResults')}
                          </Typography>
                        </div>
                      )}

                      {typeof activeItemIndex === 'number' &&
                        !isLaptop &&
                        destinationMapHotels && (
                          <HotelCard
                            className={styles.mapCard}
                            {...destinationMapHotelCardCollectionAdapter(
                              destinationMapHotels,
                            )[activeItemIndex]}
                            variant="compact"
                          />
                        )}
                    </React.Fragment>
                  ) : (
                    <div className={styles.placeholder}>
                      {placeholderImage && (
                        <Image
                          alt={placeholderImage.altText}
                          className={styles.placeholderImage}
                          focalPoint={placeholderImage.focalPoint}
                          height={459}
                          src={placeholderImage.url}
                          srcSet={{
                            desktop: {
                              height: 656,
                              width: 656,
                            },
                            laptop: {
                              height: 619,
                              width: 619,
                            },
                            tablet: {
                              height: 514,
                              width: 720,
                            },
                          }}
                          unoptimized
                          width={328}
                        />
                      )}

                      <Button
                        className={styles.placeholderButton}
                        onClick={() => setShowMap(true)}
                        text={t('items.common.words.showMap')}
                      />
                    </div>
                  )}
                </div>
              </Column>
            )}
          </Row>
        </Container>
      </div>
    </ModuleWrapper>
  );
};

DestinationMap.displayName = 'DestinationMap';

export default DestinationMap;
